/**
 * Lexer Framework - TypeScript port
 *
 * A configurable tokenizer framework adapted from the lex library.
 * Provides Token, Descriptor, and Lexer classes for building
 * language-specific tokenizers via regex-based state checks.
 *
 * Usage: Define a Descriptor with flags, token types, and state checks,
 * then create a Lexer instance to iterate tokens via get_token().
 */

export class Token {
  text: string
  type: number
  flags: number
  state: number

  constructor(text: string, type: number, flags: number, state: number) {
    this.text = text
    this.type = type
    this.flags = flags
    this.state = state
  }

  static dummy(): Token {
    return new Token('', -1, 0, -1)
  }
}

export type CheckFn = (this: Lexer, p: number) => [number, number] | null
export type CreateFn = (this: Lexer, flags: number, p: number) => Token | null
export type StateCheck = [CheckFn, CreateFn]

export class Descriptor {
  flags: Record<string, number>
  flagNames: string[]
  flagIgnore: number
  typeFlags: number[]
  typeNames: string[]
  states: Record<string, number>
  stateNames: string[]
  stateChecks: StateCheck[][]
  stateFlags: number[]
  onNew: ((this: Lexer) => void) | null;

  // Dynamic type index properties (set by defineTypes)
  [key: string]: unknown

  constructor(flagNames: string[], flagIgnore: string) {
    this.flags = { NONE: 0 }
    this.flagNames = ['NONE']
    this.flagIgnore = -1
    this.typeFlags = []
    this.typeNames = []
    this.states = {}
    this.stateNames = []
    this.stateChecks = []
    this.stateFlags = []
    this.onNew = null

    for (let i = 0; i < flagNames.length; i++) {
      this.flags[flagNames[i]] = 1 << i
      this.flagNames.push(flagNames[i])
    }

    this.flagIgnore = this.flags[flagIgnore] ?? -1
  }

  defineTypes(types: Record<string, number>): void {
    let i = 0
    for (const k in types) {
      this.typeFlags.push(types[k])
      this.typeNames.push(k)
      ;(this as Record<string, unknown>)[k] = i++
    }
  }

  defineStateNames(names: string[]): void {
    this.stateNames = names
    for (let i = 0; i < names.length; i++) {
      this.states[names[i]] = i
    }
  }

  defineState(checks: StateCheck[], flags: number): void {
    this.stateChecks.push(checks)
    this.stateFlags.push(flags)
  }

  typeToString(tokenType: number): string {
    return this.typeNames[tokenType] || ''
  }

  flagsToString(flags: number): string {
    if (flags === 0) return this.flagNames[0]

    let s = ''
    let f = 0x1
    for (let i = 1; i < this.flagNames.length; i++) {
      if ((flags & f) !== 0) {
        if (s.length > 0) s += ' | '
        s += this.flagNames[i]
      }
      f <<= 1
    }
    return s
  }
}

export class Lexer {
  descriptor: Descriptor
  pos: number
  text: string
  state: number
  previous: Token
  previousActual: Token
  tokenId: number
  extraFlags: number

  constructor(descriptor: Descriptor, text: string) {
    this.descriptor = descriptor
    this.pos = 0
    this.text = text
    this.state = 0
    this.previous = Token.dummy()
    this.previousActual = this.previous
    this.tokenId = 0
    this.extraFlags = 0

    if (descriptor.onNew !== null) {
      descriptor.onNew.call(this)
    }
  }

  createToken(tokenType: number, flags: number, end: number): Token {
    const token = new Token(
      this.text.substring(this.pos, end),
      tokenType,
      this.descriptor.typeFlags[tokenType] |
        this.descriptor.stateFlags[this.state] |
        this.extraFlags |
        flags,
      this.state,
    )

    this.pos = end

    // Non-ignored tokens update previous and increment the token counter
    if (
      (token.flags & this.descriptor.flagIgnore) !==
      this.descriptor.flagIgnore
    ) {
      this.previous = token
      ++this.tokenId
    }

    this.previousActual = token
    return token
  }

  getToken(): Token | null {
    if (this.pos >= this.text.length) return null

    const checks = this.descriptor.stateChecks[this.state]
    const p = this.pos
    const iMax = checks.length - 1

    // Try each check in order (except the last/default)
    for (let i = 0; i < iMax; i++) {
      const [checkFn, createFn] = checks[i]
      const m = checkFn.call(this, p)
      if (m !== null) {
        const t = createFn.call(this, m[0], m[1])
        if (t !== null) return t
      }
    }

    // Default: advance by at least 1 char, optionally extending
    let defaultP = p + 1
    let flags = 0
    const [defaultCheck, defaultCreate] = checks[iMax]
    const m = defaultCheck.call(this, defaultP)
    if (m !== null) {
      flags = m[0]
      defaultP = m[1]
    }
    return defaultCreate.call(this, flags, defaultP)
  }
}

// --- Utility check/create functions ---

/**
 * Create a check function that matches a regex pattern at the given position.
 * Returns [0, endPosition] if matched, null otherwise.
 */
export function checkRegex(pattern: string, regexFlags?: string): CheckFn {
  const flags = (regexFlags === undefined ? '' : regexFlags) + 'g'
  const re = new RegExp('(' + pattern + ')?', flags)

  return function (this: Lexer, p: number): [number, number] | null {
    re.lastIndex = p
    const m = re.exec(this.text)
    return m && m[1] !== undefined ? [0, m.index + m[0].length] : null
  }
}

/**
 * Create a check function that always returns null (default fallback).
 */
export function checkNull(): CheckFn {
  return function (): null {
    return null
  }
}

/**
 * Create a create function that produces a token of the given type.
 */
export function createToken(tokenType: number): CreateFn {
  return function (this: Lexer, flags: number, p: number): Token {
    return this.createToken(tokenType, flags, p)
  }
}

/**
 * Create a create function that produces a token and changes the lexer state.
 */
export function createTokenChangeState(
  tokenType: number,
  newState: number,
): CreateFn {
  return function (this: Lexer, flags: number, p: number): Token {
    const t = this.createToken(tokenType, flags, p)
    this.state = newState
    return t
  }
}
